import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Package, AlertCircle, Settings, Search, ShoppingCart, Image } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmtCur = (v) => `€${Number(v || 0).toFixed(2)}`;

const CATEGORIEN = ['Borstels', 'Chemie', 'Techniek', 'Shop producten', 'Overig'];
const EENHEDEN = ['stuks', 'jerrycans', 'flessen', 'dozen', 'rollen', 'pakken'];

// ── Leverancierscatalogus Component ──────────────────────────────
const LeveranciersCatalogus = () => {
  const [products, setProducts] = useState([]);
  const [leveranciers, setLeveranciers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLev, setSelectedLev] = useState('alle');
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/producten-catalogus`, { headers: { 'Content-Type': 'application/json' } }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/leveranciers-centraal`, { headers: { 'Content-Type': 'application/json' } }).then(r => r.ok ? r.json() : []),
    ]).then(([prods, levs]) => {
      setProducts(prods);
      setLeveranciers(levs);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = products.filter(p => {
    if (selectedLev !== 'alle' && p.leverancier_id !== selectedLev) return false;
    if (search && !p.naam.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by leverancier
  const grouped = {};
  filtered.forEach(p => {
    const key = p.leverancier_naam || 'Onbekend';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(p);
  });

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#1f2548] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="leveranciers-catalogus">
      {/* Stats */}
      <div className="flex gap-3 flex-wrap">
        <Badge className="bg-[#1f2548] text-white px-3 py-1">{products.length} producten</Badge>
        <Badge variant="outline" className="px-3 py-1">{leveranciers.length} leveranciers</Badge>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap bg-white rounded-xl border p-3">
        <Select value={selectedLev} onValueChange={setSelectedLev}>
          <SelectTrigger className="w-52" data-testid="catalogus-lev-filter"><SelectValue placeholder="Leverancier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle leveranciers</SelectItem>
            {leveranciers.map(l => <SelectItem key={l._id} value={l._id}>{l.bedrijfsnaam}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Zoek product..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="catalogus-search" />
        </div>
        <span className="text-sm text-slate-400 self-center">{filtered.length} resultaten</span>
      </div>

      {/* Products by Leverancier */}
      {Object.keys(grouped).length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-400">Geen producten gevonden</CardContent></Card>
      ) : (
        Object.entries(grouped).sort((a, b) => b[1].length - a[1].length).map(([levNaam, prods]) => (
          <Card key={levNaam}>
            <CardHeader className="bg-slate-50 border-b py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-[#1f2548]">{levNaam}</CardTitle>
                <Badge variant="outline">{prods.length} producten</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {prods.map(p => (
                  <div key={p._id} className="rounded-lg border border-slate-200 p-2 hover:shadow-sm transition-shadow" data-testid="catalogus-product-card">
                    {p.foto_url ? (
                      <img src={p.foto_url} alt="" className="w-full h-20 object-contain rounded bg-white mb-1.5" loading="lazy" />
                    ) : (
                      <div className="w-full h-20 bg-slate-100 rounded flex items-center justify-center mb-1.5">
                        <Image className="w-6 h-6 text-slate-300" />
                      </div>
                    )}
                    <p className="text-xs font-medium text-slate-800 truncate" title={p.naam}>{p.naam}</p>
                    <p className="text-[10px] text-slate-400 truncate">{p.beschrijving}</p>
                    <p className="text-sm font-bold text-[#1f2548] mt-0.5">{fmtCur(p.prijs_excl_btw)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

const Productcatalogus = () => {
  const [mainTab, setMainTab] = useState('catalogus');
  const [producten, setProducten] = useState([]);
  const [leverancierCategorieen, setLeverancierCategorieen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategorieModal, setShowCategorieModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    fetchProducten();
    fetchLeverancierCategorieen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducten = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/voorraad-producten`);
      setProducten(response.data);
    } catch (error) {
      console.error('Error fetching producten:', error);
      toast.error('Fout bij ophalen producten');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeverancierCategorieen = async () => {
    try {
      const response = await axios.get(`${API}/leverancier-categorien`);
      setLeverancierCategorieen(response.data);
    } catch (error) {
      console.error('Error fetching leverancier categorieën:', error);
      // If no categories exist, seed them
      if (error.response?.status === 404 || (Array.isArray(error.response?.data) && error.response.data.length === 0)) {
        await seedLeverancierCategorieen();
      }
    }
  };

  const seedLeverancierCategorieen = async () => {
    try {
      await axios.post(`${API}/seed-leverancier-categorien`);
      const response = await axios.get(`${API}/leverancier-categorien`);
      setLeverancierCategorieen(response.data);
    } catch (error) {
      console.error('Error seeding leverancier categorieën:', error);
    }
  };

  const handleAdd = (categorie = null) => {
    setEditingProduct(categorie ? { categorie } : null);
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleDelete = async (productId, productNaam) => {
    if (!window.confirm(`Weet je zeker dat je "${productNaam}" wilt verwijderen?\n\nLet op: Dit product wordt niet meer getoond bij het toevoegen van voorraad.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/voorraad-producten/${productId}`);
      toast.success('Product verwijderd');
      fetchProducten();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const handleToggleActief = async (product) => {
    try {
      await axios.put(`${API}/voorraad-producten/${product._id}`, {
        actief: !product.actief
      });
      toast.success(product.actief ? 'Product gedeactiveerd' : 'Product geactiveerd');
      fetchProducten();
    } catch (error) {
      console.error('Error toggling actief:', error);
      toast.error('Fout bij bijwerken');
    }
  };

  // Group products by category
  const groupedProducten = producten.reduce((acc, product) => {
    const cat = product.categorie || 'Overig';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  // Sort categories
  const sortedCategories = CATEGORIEN.filter(cat => groupedProducten[cat]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1f2548]">Productcatalogus Beheer</h1>
        <p className="text-slate-500">Beheer de centrale productlijst voor alle locaties</p>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="catalogus-tabs">
        <button onClick={() => setMainTab('catalogus')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${mainTab === 'catalogus' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <ShoppingCart className="w-4 h-4 inline mr-1" /> Leverancierscatalogus
        </button>
        <button onClick={() => setMainTab('voorraad')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${mainTab === 'voorraad' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          <Package className="w-4 h-4 inline mr-1" /> Voorraadproducten
        </button>
      </div>

      {mainTab === 'catalogus' && <LeveranciersCatalogus />}
      
      {mainTab === 'voorraad' && (
        <>
      {/* Keep existing voorraad content below */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setShowCategorieModal(true)}>
          <Settings className="w-4 h-4 mr-2" />
          Categorieën beheren
        </Button>
        <Button onClick={() => handleAdd()}>
          <Plus className="w-4 h-4 mr-2" />
          Nieuw Product
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900">
                <strong>Let op:</strong> Producten die hier worden toegevoegd zijn beschikbaar voor alle locaties.
                Als je een product deactiveert, is deze niet meer zichtbaar bij het toevoegen van voorraad.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {CATEGORIEN.map(cat => {
          const count = groupedProducten[cat]?.length || 0;
          const actief = groupedProducten[cat]?.filter(p => p.actief).length || 0;
          return (
            <Card key={cat}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-800">{count}</div>
                  <div className="text-sm text-slate-500">{cat}</div>
                  {count > 0 && (
                    <div className="text-xs text-slate-400 mt-1">
                      {actief} actief
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Products by Category */}
      {sortedCategories.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">Nog geen producten toegevoegd</p>
            <Button onClick={() => handleAdd()}>
              Eerste product toevoegen
            </Button>
          </CardContent>
        </Card>
      ) : (
        sortedCategories.map(categorie => (
          <Card key={categorie}>
            <CardHeader className="bg-slate-50 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  {categorie === 'Borstels' && '🧹'}
                  {categorie === 'Chemie' && '🧪'}
                  {categorie === 'Techniek' && '🔧'}
                  {categorie === 'Shop producten' && '🛒'}
                  {categorie === 'Overig' && '📦'}
                  {categorie}
                  <Badge variant="secondary" className="ml-2">
                    {groupedProducten[categorie].length} producten
                  </Badge>
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAdd(categorie)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Product toevoegen
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Naam</TableHead>
                    <TableHead>Leverancier</TableHead>
                    <TableHead>Eenheid</TableHead>
                    <TableHead>Varianten</TableHead>
                    <TableHead>Volgorde</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedProducten[categorie]
                    .sort((a, b) => (a.volgorde || 0) - (b.volgorde || 0))
                    .map((product) => (
                      <TableRow key={product._id}>
                        <TableCell className="font-medium">{product.naam}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {product.leverancierNaam ? (
                              <span className="text-slate-700 text-sm font-medium">{product.leverancierNaam}</span>
                            ) : (
                              <span className="text-slate-400 text-sm italic">Niet ingesteld</span>
                            )}
                            {product.leverancierCategorie && (
                              <div>
                                <Badge variant="outline" className="text-xs">
                                  {product.leverancierCategorie}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{product.eenheid}</TableCell>
                        <TableCell>
                          {product.heeftVarianten ? (
                            <div className="flex flex-wrap gap-1">
                              {product.varianten.map((variant, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {variant}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600">{product.volgorde || 0}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={product.actief}
                              onCheckedChange={() => handleToggleActief(product)}
                              className="scale-75"
                            />
                            <span className={`text-sm ${product.actief ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {product.actief ? 'Actief' : 'Inactief'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(product)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(product._id, product.naam)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}

      <ProductModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingProduct(null);
        }}
        product={editingProduct}
        onUpdate={fetchProducten}
        leverancierCategorieen={leverancierCategorieen}
        onAddCategorie={() => setShowCategorieModal(true)}
      />

      <CategorieModal
        open={showCategorieModal}
        onClose={() => setShowCategorieModal(false)}
        categorien={leverancierCategorieen}
        onUpdate={fetchLeverancierCategorieen}
      />
      </>
      )}
    </div>
  );
};

const ProductModal = ({ open, onClose, product, onUpdate, leverancierCategorieen = [], onAddCategorie }) => {
  const [formData, setFormData] = useState({
    categorie: '',
    naam: '',
    eenheid: 'stuks',
    heeftVarianten: false,
    varianten: [],
    actief: true,
    volgorde: 0,
    leverancierCategorie: '',
    leverancierNaam: ''
  });
  const [variantInput, setVariantInput] = useState('');

  useEffect(() => {
    if (product) {
      setFormData({
        categorie: product.categorie || '',
        naam: product.naam || '',
        eenheid: product.eenheid || 'stuks',
        heeftVarianten: product.heeftVarianten || false,
        varianten: product.varianten || [],
        actief: product.actief !== undefined ? product.actief : true,
        volgorde: product.volgorde || 0,
        leverancierCategorie: product.leverancierCategorie || '',
        leverancierNaam: product.leverancierNaam || ''
      });
    } else {
      setFormData({
        categorie: '',
        naam: '',
        eenheid: 'stuks',
        heeftVarianten: false,
        varianten: [],
        actief: true,
        volgorde: 0,
        leverancierCategorie: '',
        leverancierNaam: ''
      });
    }
    setVariantInput('');
  }, [product, open]);

  const handleSubmit = async () => {
    if (!formData.naam || !formData.categorie || !formData.eenheid) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    try {
      if (product?._id) {
        await axios.put(`${API}/voorraad-producten/${product._id}`, formData);
        toast.success('Product bijgewerkt');
      } else {
        await axios.post(`${API}/voorraad-producten`, formData);
        toast.success('Product toegevoegd');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddVariant = () => {
    if (!variantInput.trim()) return;
    if (formData.varianten.includes(variantInput.trim())) {
      toast.error('Deze variant bestaat al');
      return;
    }
    setFormData(prev => ({
      ...prev,
      varianten: [...prev.varianten, variantInput.trim()]
    }));
    setVariantInput('');
  };

  const handleRemoveVariant = (variant) => {
    setFormData(prev => ({
      ...prev,
      varianten: prev.varianten.filter(v => v !== variant)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {product?._id ? 'Product bewerken' : 'Nieuw product toevoegen'}
          </DialogTitle>
          <DialogDescription>
            Dit product wordt beschikbaar voor alle locaties in het voorraad systeem
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Categorie *</Label>
              <Select
                value={formData.categorie}
                onValueChange={(v) => setFormData({ ...formData, categorie: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer categorie" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIEN.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Naam *</Label>
              <Input
                value={formData.naam}
                onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                placeholder="Blokborstel"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Eenheid *</Label>
              <Select
                value={formData.eenheid}
                onValueChange={(v) => setFormData({ ...formData, eenheid: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EENHEDEN.map(eenheid => (
                    <SelectItem key={eenheid} value={eenheid}>{eenheid}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Leverancier categorie</Label>
              <Select
                value={formData.leverancierCategorie || 'none'}
                onValueChange={(v) => setFormData({ ...formData, leverancierCategorie: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer leverancier categorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Niet ingesteld</SelectItem>
                  {leverancierCategorieen.map(cat => (
                    <SelectItem key={cat._id} value={cat.naam}>{cat.naam}</SelectItem>
                  ))}
                  <div className="border-t my-1"></div>
                  <div 
                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm text-blue-600 hover:bg-slate-100"
                    onClick={() => {
                      onClose();
                      onAddCategorie();
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuwe categorie toevoegen
                  </div>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Bij welk type leverancier wordt dit product besteld?</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Standaard leverancier</Label>
              <Input
                value={formData.leverancierNaam}
                onChange={(e) => setFormData({ ...formData, leverancierNaam: e.target.value })}
                placeholder="Bijv. Kenotek B.V."
              />
              <p className="text-xs text-slate-500 mt-1">Welke leverancier levert dit product?</p>
            </div>
            <div>
              <Label>Volgorde</Label>
              <Input
                type="number"
                value={formData.volgorde}
                onChange={(e) => setFormData({ ...formData, volgorde: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
              <p className="text-xs text-slate-500 mt-1">Bepaalt de sortering binnen de categorie</p>
            </div>
          </div>


          <div className="border-t pt-4">
            <div className="flex items-center space-x-2 mb-3">
              <Switch
                checked={formData.heeftVarianten}
                onCheckedChange={(v) => setFormData({ ...formData, heeftVarianten: v })}
              />
              <Label>Product heeft varianten</Label>
            </div>

            {formData.heeftVarianten && (
              <div className="pl-6 space-y-3">
                <p className="text-sm text-slate-500">
                  Bijvoorbeeld voor Poppy geur potjes: Vanille, Lavendel, Kers, etc.
                </p>
                
                <div className="flex gap-2">
                  <Input
                    value={variantInput}
                    onChange={(e) => setVariantInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddVariant();
                      }
                    }}
                    placeholder="Variant naam"
                  />
                  <Button type="button" onClick={handleAddVariant}>
                    Toevoegen
                  </Button>
                </div>

                {formData.varianten.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.varianten.map((variant, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1">
                        {variant}
                        <button
                          onClick={() => handleRemoveVariant(variant)}
                          className="ml-1 hover:text-red-600"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.actief}
                onCheckedChange={(v) => setFormData({ ...formData, actief: v })}
              />
              <Label>Product is actief</Label>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Inactieve producten zijn niet zichtbaar bij het toevoegen van voorraad
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button onClick={handleSubmit}>
            {product?._id ? 'Bijwerken' : 'Toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Modal for managing leverancier categories
const CategorieModal = ({ open, onClose, categorien, onUpdate }) => {
  const [newCategorie, setNewCategorie] = useState('');
  const [editingCategorie, setEditingCategorie] = useState(null);
  const [editValue, setEditValue] = useState('');

  const handleAdd = async () => {
    if (!newCategorie.trim()) {
      toast.error('Voer een categorienaam in');
      return;
    }

    try {
      await axios.post(`${API}/leverancier-categorien`, { naam: newCategorie.trim() });
      toast.success('Categorie toegevoegd');
      setNewCategorie('');
      onUpdate();
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error(error.response?.data?.detail || 'Fout bij toevoegen');
    }
  };

  const handleUpdate = async (id) => {
    if (!editValue.trim()) {
      toast.error('Voer een categorienaam in');
      return;
    }

    try {
      await axios.put(`${API}/leverancier-categorien/${id}`, { naam: editValue.trim() });
      toast.success('Categorie bijgewerkt');
      setEditingCategorie(null);
      setEditValue('');
      onUpdate();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Fout bij bijwerken');
    }
  };

  const handleDelete = async (id, naam) => {
    if (!window.confirm(`Weet je zeker dat je "${naam}" wilt verwijderen?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/leverancier-categorien/${id}`);
      toast.success('Categorie verwijderd');
      onUpdate();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const startEdit = (cat) => {
    setEditingCategorie(cat._id);
    setEditValue(cat.naam);
  };

  const cancelEdit = () => {
    setEditingCategorie(null);
    setEditValue('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Leverancier Categorieën Beheren</DialogTitle>
          <DialogDescription>
            Beheer de categorieën voor leveranciers. Deze worden gebruikt om producten aan leveranciers te koppelen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Add new category */}
          <div className="flex gap-2">
            <Input
              value={newCategorie}
              onChange={(e) => setNewCategorie(e.target.value)}
              placeholder="Nieuwe categorie..."
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <Button onClick={handleAdd}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* List of categories */}
          <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
            {categorien.length === 0 ? (
              <div className="p-4 text-center text-slate-500">
                Geen categorieën gevonden
              </div>
            ) : (
              categorien.map((cat) => (
                <div key={cat._id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                  {editingCategorie === cat._id ? (
                    <div className="flex gap-2 flex-1 mr-2">
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdate(cat._id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                      />
                      <Button size="sm" onClick={() => handleUpdate(cat._id)}>
                        Opslaan
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>
                        Annuleren
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="font-medium">{cat.naam}</span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(cat)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(cat._id, cat.naam)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Productcatalogus;
